import type { CSSProperties } from 'react';

import { EventCalendar } from '@nesso/ui/blocks/event-calendar/event-calendar';
import { EventCalendarContent } from '@nesso/ui/blocks/event-calendar/event-calendar-content';
import { cn } from '@nesso/ui/lib/utils';

type Props = {
    selectedDay: Date;
    view: 'week' | 'day';
};

export function CalendarPageTimeGrid({ selectedDay, view }: Props) {
    const isWeek = view === 'week';

    return (
        <EventCalendar
            // No bottom padding: the hours run under the card's edge, as in Figma.
            className={cn('flex-1 min-h-0', isWeek ? 'px-4 pt-4' : 'p-2')}
            events={[]}
            view={view}
            date={selectedDay}
            weekStartsOn={0}
            nowIndicator={false}
            interactions={{ selectSlot: false }}
            scrollToHour={8}
            // The 8 AM label hangs 8 px above its line; this puts it flush with the top.
            scrollToHourOffset={8}
            showAllDay={false}
            showDayHeader={isWeek}
            // --ec-hour-height has no effect here: the engine redeclares it via viewConfig.hourHeight.
            hourHeight={isWeek ? '55px' : '61px'}
            style={
                {
                    '--ec-gutter-width': isWeek ? '5rem' : '51px',
                    // The phone's hour lines start at its label column's edge, as in Figma.
                    ...(isWeek && { '--ec-gutter-line-overhang': '29px' }),
                } as CSSProperties
            }
            classNames={{
                timeGrid: 'border-t-0',
                timeGridHeader: 'border-b-0',
                timeGridHeaderGutter: 'border-e-0',
                ...(!isWeek && { timeGutter: 'border-e-0' }),
                dayHeader: 'mt-2 h-8 px-2 pt-0 pb-2',
                timeGutterLabel: cn(
                    isWeek ? 'end-9.25' : 'end-2',
                    'text-sm font-medium leading-none',
                ),
            }}
            renderDayHeader={({ day }) => (
                <div className="flex h-full items-center justify-center gap-6 text-base font-normal text-muted-foreground">
                    <span className="uppercase">
                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>{' '}
                    <span>{day.getDate()}</span>
                </div>
            )}
        >
            <EventCalendarContent />
        </EventCalendar>
    );
}
